/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { Suspense, lazy } from "react";
import PropTypes from "prop-types";
import loadingIcon from "../../../assets/icons/loading.svg";
import { SvgHandler } from "../svg-handler/svg-handler";
import hamburgerIcon from "../../../assets/icons/hamburger.svg";
import closeIcon from "../../../assets/icons/social/close.svg";
import { CSSTransition } from "react-transition-group";
import { getMenuData } from "./header-helper-components";
import { isEmpty } from "../../utils";
import get from "lodash/get";

class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    this.SidebarTemplate = null;
    this.state = {
      loadSidebar: false,
      loadingIndicator: false
    };
  }

  componentDidUpdate(prevProp) {
    const wasSidebarVisible = get(prevProp, ["header", "isSidebarVisible"]);
    const { isSidebarVisible } = this.props.header;
    if (isSidebarVisible !== wasSidebarVisible && isSidebarVisible) {
      this.dynamicImport();
    }
  }

  async dynamicImport() {
    if (isEmpty(this.SidebarTemplate)) {
      this.setState({ loadSidebar: false });
      this.SidebarTemplate = await lazy(() => import(/* webpackChunkName: "SidebarTemplate" */ "./sidebar/index"));
    }
    this.setState({ loadingIndicator: true });
  }

  render() {
    const {
      dataTestId,
      toggleSidebar,
      closeAll,
      publisherName,
      menu,
      domainSlug,
      socialLinks,
      publisherTheme,
      header = {},
      headerClass = "",
      pageBuilderConfig,
      socialFollowStyle,
      appStoreDownloadLink,
      googlePlayDownloadLink
    } = this.props;
    const { isSidebarVisible } = header;
    const { loadSidebar, loadingIndicator } = this.state;
    const isMobile = get(global, "document.documentElement.clientWidth") <= 767;
    const displayStatus = () => {
      if (!isSidebarVisible) {
        return <SvgHandler xlinkHref={hamburgerIcon} width="24" height="24" svgWrapperClazzName="svg-wrapper" />;
      } else if (isSidebarVisible && loadingIndicator) {
        this.setState({ loadSidebar: true, loadingIndicator: false });
        return <SvgHandler xlinkHref={loadingIcon} width="24" height="24" svgWrapperClazzName="svg-wrapper" />;
      } else if (isSidebarVisible && loadSidebar) {
        return (
          <SvgHandler
            xlinkHref={closeIcon}
            clazzName="closeIcon"
            width="24"
            height="24"
            svgWrapperClazzName="svg-wrapper"
          />
        );
      } else if (isSidebarVisible && loadSidebar && isMobile) {
        return <SvgHandler xlinkHref={hamburgerIcon} width="24" height="24" svgWrapperClazzName="svg-wrapper" />;
      }
    };
    return (
      <>
        <div data-test-id={dataTestId} onClick={toggleSidebar}>
          <div>{displayStatus()}</div>
          <CSSTransition in={isSidebarVisible} timeout={{ enter: 300, exit: 500 }} classNames="side-bar-transition">
            <div>
              {loadSidebar ? (
                <Suspense fallback={null}>
                  <this.SidebarTemplate
                    customCssClasses=""
                    isSidebarVisible={isSidebarVisible}
                    closeSidebar={toggleSidebar}
                    closeAll={closeAll}
                    menuItems={getMenuData(menu, "sidebar-menu", domainSlug)}
                    publisherName={publisherName}
                    languageDirection={get(publisherTheme, ["language_direction"], "left")}
                    socialLinks={socialLinks}
                    publisherTheme={publisherTheme}
                    headerClass={headerClass}
                    pageBuilderConfig={pageBuilderConfig}
                    socialStyle={socialFollowStyle}
                    appStoreDownloadLink={appStoreDownloadLink}
                    googlePlayDownloadLink={googlePlayDownloadLink}
                  />
                </Suspense>
              ) : null}
            </div>
          </CSSTransition>
        </div>
      </>
    );
  }
}
Sidebar.propTypes = {
  header: PropTypes.object,
  toggleSidebar: PropTypes.func,
  menu: PropTypes.object,
  domainSlug: PropTypes.string,
  closeAll: PropTypes.func,
  isSidebarVisible: PropTypes.bool,
  publisherName: PropTypes.string,
  socialLinks: PropTypes.object,
  publisherTheme: PropTypes.string,
  dataTestId: PropTypes.string,
  headerClass: PropTypes.string,
  pageBuilderConfig: PropTypes.object,
  socialFollowStyle: PropTypes.object,
  enableAppDownloadLinks: PropTypes.bool,
  appStoreDownloadLink: PropTypes.string,
  googlePlayDownloadLink: PropTypes.string
};

export { Sidebar };
